import React, { useState } from 'react';
import PropTypes from 'prop-types';

const PaymentForm = ({ amount, onPaymentSubmit, isProcessing }) => {
  const [formData, setFormData] = useState({
    cardName: '',
    cardNumber: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
  });
  
  const [errors, setErrors] = useState({});

  const { cardName, cardNumber, expiryMonth, expiryYear, cvv } = formData;

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Card number formatting - only numbers and automatically add spaces
    if (name === 'cardNumber') {
      const cleaned = value.replace(/\D/g, '');
      const formatted = cleaned.replace(/(\d{4})(?=\d)/g, '$1 ');
      setFormData({ ...formData, [name]: formatted.substring(0, 19) });
    } else if (name === 'cvv') {
      const cleaned = value.replace(/\D/g, '');
      setFormData({ ...formData, [name]: cleaned.substring(0, 4) });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!cardName.trim()) {
      newErrors.cardName = 'Cardholder name is required';
    }
    
    if (!cardNumber.trim()) {
      newErrors.cardNumber = 'Card number is required';
    } else if (cardNumber.replace(/\s/g, '').length < 16) {
      newErrors.cardNumber = 'Invalid card number';
    }
    
    if (!expiryMonth) {
      newErrors.expiryMonth = 'Month is required';
    }
    
    if (!expiryYear) {
      newErrors.expiryYear = 'Year is required';
    } else {
      // Check if card is expired
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;
      
      if (parseInt(expiryYear) < currentYear || 
         (parseInt(expiryYear) === currentYear && parseInt(expiryMonth) < currentMonth)) {
        newErrors.expiryYear = 'Card is expired';
      }
    }
    
    if (!cvv) {
      newErrors.cvv = 'CVV is required';
    } else if (cvv.length < 3) {
      newErrors.cvv = 'Invalid CVV';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Process the payment
      onPaymentSubmit({
        cardName,
        cardNumber: cardNumber.replace(/\s/g, ''),
        expiryMonth,
        expiryYear,
        cvv
      });
    }
  };

  // Generate month options
  const monthOptions = [];
  for (let i = 1; i <= 12; i++) {
    const month = i.toString().padStart(2, '0');
    monthOptions.push(
      <option key={month} value={month}>
        {month}
      </option>
    );
  }

  // Generate year options (current year + 10 years)
  const yearOptions = [];
  const currentYear = new Date().getFullYear();
  for (let i = 0; i < 10; i++) {
    const year = (currentYear + i).toString();
    yearOptions.push(
      <option key={year} value={year}>
        {year}
      </option>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="payment-form">
      <div className="mb-3">
        <label htmlFor="amount" className="form-label">Amount</label>
        <div className="input-group">
          <span className="input-group-text">$</span>
          <input 
            type="text" 
            className="form-control" 
            value={amount}
            readOnly
          />
        </div>
      </div>
      
      <div className="mb-3">
        <label htmlFor="cardName" className="form-label">Cardholder Name</label>
        <input
          type="text"
          className={`form-control ${errors.cardName ? 'is-invalid' : ''}`}
          id="cardName"
          name="cardName"
          value={cardName}
          onChange={handleChange}
          placeholder="John Doe"
        />
        {errors.cardName && <div className="invalid-feedback">{errors.cardName}</div>}
      </div>
      
      <div className="mb-3">
        <label htmlFor="cardNumber" className="form-label">Card Number</label>
        <input
          type="text"
          className={`form-control ${errors.cardNumber ? 'is-invalid' : ''}`}
          id="cardNumber"
          name="cardNumber"
          value={cardNumber}
          onChange={handleChange}
          placeholder="XXXX XXXX XXXX XXXX"
        />
        {errors.cardNumber && <div className="invalid-feedback">{errors.cardNumber}</div>}
      </div>
      
      <div className="row mb-3">
        <div className="col-md-8">
          <label className="form-label">Expiry Date</label>
          <div className="input-group">
            <select
              className={`form-select ${errors.expiryMonth ? 'is-invalid' : ''}`}
              name="expiryMonth"
              value={expiryMonth}
              onChange={handleChange}
            >
              <option value="">Month</option>
              {monthOptions}
            </select>
            <select
              className={`form-select ${errors.expiryYear ? 'is-invalid' : ''}`}
              name="expiryYear"
              value={expiryYear}
              onChange={handleChange}
            >
              <option value="">Year</option>
              {yearOptions}
            </select>
            {(errors.expiryMonth || errors.expiryYear) && (
              <div className="invalid-feedback d-block">
                {errors.expiryMonth || errors.expiryYear}
              </div>
            )}
          </div>
        </div>
        
        <div className="col-md-4">
          <label htmlFor="cvv" className="form-label">CVV</label>
          <input
            type="text"
            className={`form-control ${errors.cvv ? 'is-invalid' : ''}`}
            id="cvv"
            name="cvv"
            value={cvv}
            onChange={handleChange}
            placeholder="XXX"
          />
          {errors.cvv && <div className="invalid-feedback">{errors.cvv}</div>}
        </div>
      </div>
      
      <div className="mb-3 form-check">
        <input type="checkbox" className="form-check-input" id="saveCard" />
        <label className="form-check-label" htmlFor="saveCard">Save card for future payments</label>
      </div>
      
      <div className="d-grid">
        <button
          type="submit"
          className="btn btn-primary btn-lg"
          disabled={isProcessing}
        >
          {isProcessing ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Processing...
            </>
          ) : (
            <>Pay ${amount} Now</>
          )}
        </button>
      </div>
      
      <div className="mt-3 text-center">
        <small className="text-muted">
          <i className="fas fa-lock me-2"></i>
          Your payment info is secured with SSL encryption
        </small>
      </div>
    </form>
  );
};

PaymentForm.propTypes = {
  amount: PropTypes.number.isRequired,
  onPaymentSubmit: PropTypes.func.isRequired,
  isProcessing: PropTypes.bool.isRequired
};

export default PaymentForm;
